import type { CSSProperties, FunctionComponent, ReactNode } from 'react';

const baseStyles: Pick<CSSProperties, 'display' | 'flexDirection' | 'alignItems' | 'justifyContent'> = {
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
};

type FlexProps = {
  children: ReactNode;
  className?: string;
  direction?: 'row' | 'column';
  align?: CSSProperties['alignItems'];
  justify?: CSSProperties['justifyContent'];
  grow?: CSSProperties['flexGrow'];
  shrink?: CSSProperties['flexShrink'];
  wrap?: CSSProperties['flexWrap'];
  gap?: CSSProperties['gap'];
  order?: CSSProperties['order'];
  isInlineFlex?: boolean;
  basis?: CSSProperties['flexBasis'];
};

/**
 * Flex is a simple, generic and reusable flexbox component.
 *
 * @example
 * <Flex>
 *   <div>One</div>
 *   <div>Two</div>
 *   <div>Three</div>
 * </Flex>
 */
const Flex: FunctionComponent<FlexProps> = ({
  children,
  className,
  direction = 'row',
  align = 'center',
  justify = 'center',
  grow,
  shrink,
  wrap,
  gap,
  order,
  isInlineFlex,
  basis = 'auto',
}) => (
  <div
    className={className}
    style={{
      ...baseStyles,
      flexDirection: direction,
      alignItems: align,
      justifyContent: justify,
      flexGrow: grow,
      flexShrink: shrink,
      flexWrap: wrap,
      flexBasis: basis,
      gap,
      order,
      display: isInlineFlex ? 'inline-flex' : baseStyles.display,
    }}
  >
    {children}
  </div>
);

export default Flex;
